/**
 * Estilos da Área do PCP. Mesmo desenho das telas novas do Legado
 * (Relatórios, Capacidade): lista com divisórias, texto neutro e UM sinal de
 * cor por linha — cor só comunica estado. Tudo em tokens (`var(--…)`), então
 * claro e escuro saem do tema global, sem tema próprio (ADR-0087, decisão 9).
 *
 * Identificador do lote sempre em mono: diferencia 1/l/I (crítico no
 * "RIPA → RlPA") e escaneia bem em lista.
 */
import styled, { css } from "styled-components";

import { BP_MOBILE, BP_TABLET } from "../../styles/canonical/breakpoints";
import { PageContainer } from "../../styles/shared";

export const tabletAcima = `(min-width: ${BP_MOBILE + 1}px)`;
export const desktop = `(min-width: ${BP_TABLET + 1}px)`;
export const celular = `(max-width: ${BP_MOBILE}px)`;

/** Fundo escurecido de folhas e modais (o token canônico não tem um). */
export const SCRIM = "rgba(3, 4, 8, 0.55)";

const tom = (nome, parte) => `var(--${nome || "neutral"}-${parte})`;

// ------------------------------------------------------------------ página

export const Pagina = styled(PageContainer)`
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
  padding: 12px 12px calc(88px + var(--safe-area-bottom, 0px));

  @media ${tabletAcima} {
    padding: 16px 20px 40px;
    gap: 16px;
  }
`;

export const Topo = styled.div`
  position: sticky;
  top: var(--app-header-height, 0px);
  z-index: 80;
  margin: -12px -12px 0;
  padding: 10px 12px;
  background: var(--surface);
  border-bottom: 1px solid var(--border);
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;

  @media ${tabletAcima} {
    margin: -16px -20px 0;
    padding: 12px 20px;
  }
`;

export const LinhaTitulo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;

  /* Celular: as ações descem para a linha de baixo em vez de empurrar a
     página para o lado (zero rolagem lateral). */
  @media ${celular} {
    flex-wrap: wrap;
  }
`;

export const BlocoTitulo = styled.div`
  flex: 1 1 auto;
  min-width: 0;

  h1 {
    margin: 0;
    font-size: 17px;
    font-weight: 700;
    line-height: 1.2;
    color: var(--ink);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  p {
    margin: 1px 0 0;
    font-size: var(--text-min, 12px);
    color: var(--ink-2);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  @media ${desktop} {
    h1 {
      font-size: 20px;
    }
  }
`;

export const Controles = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;

  @media ${celular} {
    flex: 1 1 100%;
    justify-content: flex-end;

    .so-desktop {
      display: none;
    }
  }
`;

/** Pílula de abas/opções, igual à da Capacidade. */
export const Segmentado = styled.div`
  display: inline-flex;
  flex-wrap: nowrap;
  max-width: 100%;
  padding: 2px;
  border-radius: 9px;
  background: var(--surface-2);
  border: 1px solid var(--border);

  button {
    flex: 1 1 auto;
    min-width: 0;
    border: 0;
    background: transparent;
    padding: 0 10px;
    min-height: 28px;
    border-radius: 7px;
    font: inherit;
    font-size: 12.5px;
    font-weight: 600;
    color: var(--ink-2);
    cursor: pointer;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    justify-content: center;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  button[aria-selected="true"],
  button[aria-pressed="true"] {
    background: var(--surface);
    color: var(--ink);
    box-shadow: var(--shadow-1, 0 1px 2px rgba(0, 0, 0, 0.08));
  }

  button:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: -2px;
  }
`;

/** Abas no rodapé do celular, no alcance do polegar (decisão do design). */
export const AbasRodape = styled.nav`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 90;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  background: var(--surface);
  border-top: 1px solid var(--border);
  padding-bottom: var(--safe-area-bottom, 0px);

  a {
    min-height: 56px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2px;
    font-size: 11px;
    font-weight: 600;
    color: var(--ink-3);
    text-decoration: none;
    position: relative;
  }

  a[aria-current="page"] {
    color: var(--accent);
  }

  .contador {
    position: absolute;
    top: 6px;
    left: calc(50% + 6px);
    min-width: 16px;
    height: 16px;
    padding: 0 4px;
    border-radius: 999px;
    background: var(--accent);
    color: var(--ink-on-accent);
    font-size: 10px;
    line-height: 16px;
    text-align: center;
  }
`;

// ------------------------------------------------------------------ botões

/* Compactos (pedido do Igor, 20/10): 34 px de altura, texto 13 px. */
const baseBotao = css`
  min-height: 34px;
  padding: 0 12px;
  border-radius: 8px;
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  cursor: pointer;
  white-space: nowrap;
  text-decoration: none;
  transition: transform 80ms ease;

  &:active:not(:disabled) {
    transform: scale(0.97);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 3px solid var(--accent-ring);
    outline-offset: 2px;
  }
`;

export const BotaoPrimario = styled.button`
  ${baseBotao};
  border: 1px solid var(--accent);
  background: var(--accent);
  color: var(--ink-on-accent);
`;

export const BotaoSecundario = styled.button`
  ${baseBotao};
  border: 1px solid var(--border-strong);
  background: var(--surface);
  color: var(--ink);
`;

export const BotaoTexto = styled.button`
  ${baseBotao};
  border: 0;
  background: transparent;
  color: var(--ink-2);
  padding: 0 8px;
`;

export const BotaoPerigo = styled.button`
  ${baseBotao};
  border: 1px solid var(--danger-border);
  background: var(--danger-bg);
  color: var(--danger-text);
`;

export const BotaoIcone = styled.button`
  ${baseBotao};
  width: 34px;
  padding: 0;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--ink-2);
`;

// ------------------------------------------------------------------ lista

export const Lista = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  border: 1px solid var(--border);
  border-radius: 12px;
  background: var(--surface);
  overflow: hidden;
`;

export const Linha = styled.li`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  min-width: 0;
  border-top: 1px solid var(--border);
  ${({ $tom }) => ($tom ? `box-shadow: inset 3px 0 0 ${tom($tom, "solid")};` : "")}

  &:first-child {
    border-top: 0;
  }

  /* Celular: o texto ocupa a linha inteira e os botões vêm embaixo. */
  @media ${celular} {
    flex-wrap: wrap;
    justify-content: flex-end;
    row-gap: 8px;
  }
`;

export const Corpo = styled.div`
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;

  @media ${celular} {
    flex-basis: 100%;
  }
`;

export const TituloLinha = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: var(--ink);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const Sub = styled.div`
  font-size: 12.5px;
  color: var(--ink-2);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const Meta = styled.div`
  font-size: var(--text-min, 12px);
  color: var(--ink-3);
  display: flex;
  flex-wrap: wrap;
  column-gap: 8px;
  font-variant-numeric: tabular-nums;
`;

export const Mono = styled.span`
  font-family: var(--font-mono);
  letter-spacing: -0.01em;
`;

export const Selo = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11.5px;
  font-weight: 600;
  white-space: nowrap;
  background: ${({ $tom }) => tom($tom, "bg")};
  color: ${({ $tom }) => tom($tom, "text")};
  border: 1px solid ${({ $tom }) => tom($tom, "border")};
`;

export const Progresso = styled.div`
  height: ${({ $altura }) => $altura || 6}px;
  border-radius: 999px;
  background: var(--surface-3);
  overflow: hidden;

  span {
    display: block;
    height: 100%;
    width: ${({ $pct }) => Math.max(0, Math.min(100, $pct))}%;
    background: var(--accent);
    border-radius: inherit;
    transition: width 220ms ease;
  }

  @media (prefers-reduced-motion: reduce) {
    span {
      transition: none;
    }
  }
`;

export const Secao = styled.section`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;

  > h2 {
    margin: 0;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--ink-3);
  }
`;

export const Aviso = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid ${({ $tom }) => tom($tom, "border")};
  background: ${({ $tom }) => tom($tom, "bg")};
  color: var(--ink);
  font-size: 13px;
  line-height: 1.45;
`;

export const Campo = styled.label`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  font-size: 12px;
  font-weight: 600;
  color: var(--ink-2);

  input,
  textarea,
  select {
    font: inherit;
    font-size: 14px;
    font-weight: 400;
    color: var(--ink);
    background: var(--surface);
    border: 1px solid var(--border-strong);
    border-radius: 10px;
    padding: 7px 10px;
    min-height: 36px;
    width: 100%;
    box-sizing: border-box;
  }

  textarea {
    min-height: 88px;
    resize: vertical;
    line-height: 1.45;
  }

  input:focus-visible,
  textarea:focus-visible,
  select:focus-visible {
    outline: 3px solid var(--accent-ring);
    outline-offset: 0;
    border-color: var(--accent);
  }

  .dica {
    font-weight: 400;
    color: var(--ink-3);
  }
`;

export const Grade2 = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 10px;

  @media ${tabletAcima} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

export const Vazio = styled.p`
  margin: 0;
  padding: 24px 12px;
  text-align: center;
  color: var(--ink-3);
  font-size: 13px;
`;
